import { useState, useEffect } from 'react';
import { useMaterialTrackerPage } from '../../state/materialTracker';
import { MaterialTrackerItem } from '../../api/materialTracker';
import { toast } from 'react-toastify';
import { createSafeCloseHandler } from '../../utils/modalUtils';

interface MaterialFormProps {
  projectId: string;
  material?: MaterialTrackerItem;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const MaterialForm = ({ projectId, material, isOpen, onClose, onSuccess }: MaterialFormProps) => {
  const { createMaterial, updateMaterial } = useMaterialTrackerPage(projectId);
  const isEditing = !!material;

  const [formData, setFormData] = useState({
    item: '',
    name: '',
    description: '',
    quantity: 0,
    ordered_at: '',
    eta: '',
    tracking_number: '',
    lead_time: '',
    esd: '',
    isd: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateErrors, setDateErrors] = useState<{[key: string]: string}>({});

  // Create safe close handler
  const handleSafeClose = createSafeCloseHandler(
    formData, 
    onClose, 
    ['quantity'] // Exclude auto-filled fields (quantity defaults to 0)
  );

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleSafeClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, handleSafeClose]);

  useEffect(() => {
    if (material) {
      setFormData({
        item: material.item,
        name: material.name,
        description: material.description,
        quantity: material.quantity,
        ordered_at: material.ordered_at,
        eta: material.eta,
        tracking_number: material.tracking_number || '',
        lead_time: material.lead_time || '',
        esd: material.esd,
        isd: material.isd
      });
    } else {
      setFormData({
        item: '',
        name: '',
        description: '',
        quantity: 0,
        ordered_at: '',
        eta: '',
        tracking_number: '',
        lead_time: '',
        esd: '',
        isd: ''
      });
    }
  }, [material]);

  // Date validation logic
  const validateDates = (data: typeof formData) => {
    const errors: {[key: string]: string} = {};
    
    // Convert date strings to Date objects for comparison
    const orderedAt = data.ordered_at ? new Date(data.ordered_at) : null;
    const eta = data.eta ? new Date(data.eta) : null;
    const esd = data.esd ? new Date(data.esd) : null;
    const isd = data.isd ? new Date(data.isd) : null;
    
    // ETA cannot be before ordered date
    if (orderedAt && eta && eta < orderedAt) {
      errors.eta = 'ETA cannot be before the ordered date';
    }
    
    // ESD cannot be before ordered date
    if (orderedAt && esd && esd < orderedAt) {
      errors.esd = 'Ship date cannot be before the ordered date';
    }
    
    // ESD should logically be before or equal to ETA
    if (esd && eta && esd > eta) {
      errors.esd = 'Ship date should not be after ETA';
    }
    
    // ISD cannot be before ETA (can't install before it arrives)
    if (eta && isd && isd < eta) {
      errors.isd = 'Install date cannot be before ETA';
    }
    
    // ISD cannot be before ESD (can't install before it ships)
    if (esd && isd && isd < esd) {
      errors.isd = 'Install date cannot be before ship date';
    }
    
    return errors;
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check for date validation errors
    const errors = validateDates(formData);
    if (Object.keys(errors).length > 0) {
      setDateErrors(errors);
      toast.error('Please fix the date validation errors before submitting');
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Prepare data for submission, handling optional fields
      const submitData = {
        ...formData,
        // Convert empty strings to null for optional fields to match server schema
        tracking_number: formData.tracking_number || null,
        lead_time: formData.lead_time || null
      };

      if (isEditing && material?.id) {
        await updateMaterial(material.id, submitData);
        toast.success('Material updated successfully');
      } else {
        await createMaterial(submitData);
        toast.success('Material created successfully');
      }
      
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Form submission error:', error);
      
      // Show user-friendly error messages
      if (error.message?.includes('Server returned invalid response')) {
        toast.warning('Operation may have succeeded, but received incomplete response from server. Please refresh to see changes.');
      } else if (error.message?.includes('JSON')) {
        toast.warning('Server response was incomplete. The operation may have been successful. Please refresh the page.');
      } else if (error.message?.includes('404')) {
        toast.error('Material tracking feature is not yet implemented on the server.');
      } else if (error.message?.includes('422')) {
        toast.error('Invalid data provided. Please check all fields and try again.');
      } else if (error.message?.includes('401') || error.message?.includes('403')) {
        toast.error('You do not have permission to perform this action.');
      } else {
        toast.error(error.message || `Failed to ${isEditing ? 'update' : 'create'} material`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newFormData = {
      ...formData,
      [name]: type === 'number' ? Number(value) : value
    };
    
    setFormData(newFormData);
    
    // Validate dates after change
    const errors = validateDates(newFormData);
    setDateErrors(errors);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center p-4 z-50"
      onClick={handleSafeClose}
    >
      <button
        onClick={() => handleSafeClose()}
        className="fixed top-4 right-4 z-[60] text-[#aaa] hover:text-[#555] p-2"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div 
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-[#eaeaea]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold">
              {isEditing ? 'Edit Material' : 'Add New Material'}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">
                  Item
                </label>
                <input
                  type="text"
                  name="item"
                  value={formData.item}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#444] mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">
                  Date Ordered
                </label>
                <input
                  type="datetime-local"
                  name="ordered_at"
                  value={formData.ordered_at}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-0 ${
                    dateErrors.ordered_at
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-[#eaeaea] focus:ring-0'
                  }`}
                />
                {dateErrors.ordered_at && (
                  <p className="mt-1 text-sm text-red-600">{dateErrors.ordered_at}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">
                  ETA
                </label>
                <input
                  type="datetime-local"
                  name="eta"
                  value={formData.eta}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-0 ${
                    dateErrors.eta
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-[#eaeaea] focus:ring-0'
                  }`}
                />
                {dateErrors.eta && (
                  <p className="mt-1 text-sm text-red-600">{dateErrors.eta}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">
                  Tracking Number
                </label>
                <input
                  type="text"
                  name="tracking_number"
                  value={formData.tracking_number}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">
                  Lead Time
                </label>
                <input
                  type="text"
                  name="lead_time"
                  value={formData.lead_time}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">
                  ESD (Expected Ship Date)
                </label>
                <input
                  type="datetime-local"
                  name="esd"
                  value={formData.esd}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-0 ${
                    dateErrors.esd
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-[#eaeaea] focus:ring-0'
                  }`}
                />
                {dateErrors.esd && (
                  <p className="mt-1 text-sm text-red-600">{dateErrors.esd}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">
                  ISD (In Service Date)
                </label>
                <input
                  type="datetime-local"
                  name="isd"
                  value={formData.isd}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-0 ${
                    dateErrors.isd
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-[#eaeaea] focus:ring-0'
                  }`}
                />
                {dateErrors.isd && (
                  <p className="mt-1 text-sm text-red-600">{dateErrors.isd}</p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => handleSafeClose()}
                className="px-4 py-2 text-[#444] border border-[#eaeaea] rounded-lg hover:bg-[#fafafa]"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#171717] text-white rounded-md hover:bg-[#333] disabled:opacity-50"
                disabled={isSubmitting || Object.keys(dateErrors).length > 0}
              >
                {isSubmitting ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}; 